import React, { useState, useEffect } from 'react';
import axios from 'axios';
import GlassCard from './GlassCard';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, Zap, Shield, Globe, Cpu } from 'lucide-react';

interface A2AMessage {
  id: string;
  timestamp: string;
  sender: string;
  recipient: string;
  type: string;
  team: string;
  content: any;
  priority: string;
}

const A2ANeuralFeed: React.FC = () => {
  const [messages, setMessages] = useState<A2AMessage[]>([]);
  const [loading, setLoading] = useState(true);

  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000';

  useEffect(() => {
    const fetchFeed = async () => {
      try {
        const response = await axios.get(`${apiUrl}/api/v1/a2a/feed`);
        setMessages(response.data);
      } catch (err) {
        console.error('A2A Neural Link Sync Error', err);
      } finally {
        setLoading(false);
      }
    };

    fetchFeed();
    const interval = setInterval(fetchFeed, 3000); // 3s polling for A2A chatter
    return () => clearInterval(interval);
  }, []);

  const getTeamIcon = (team: string) => {
    switch (team.toLowerCase()) {
      case 'securities': return <Globe size={14} className="text-brand-teal" />;
      case 'crypto': return <Zap size={14} className="text-brand-plum" />;
      case 'forex': return <Shield size={14} className="text-amber-400" />;
      default: return <Cpu size={14} />;
    }
  };

  return (
    <GlassCard variant="elevated" className="h-[600px] flex flex-col p-0 overflow-hidden border-white/5">
      <div className="p-6 border-b border-white/5 bg-white/[0.02] flex justify-between items-center">
        <div className="flex items-center gap-3">
           <MessageSquare size={18} className="text-brand-teal animate-pulse" />
           <h3 className="text-xs font-black uppercase tracking-widest text-text-primary">Neural <span className="text-brand-teal-light">Stream Log</span></h3>
        </div>
        <div className="flex items-center gap-2">
           <div className="w-1.5 h-1.5 rounded-full bg-brand-teal animate-pulse shadow-glow" />
           <span className="text-[9px] font-black uppercase tracking-widest text-text-tertiary">Live Sync Active</span>
        </div>
      </div>

      <div className="flex-grow overflow-y-auto p-6 space-y-4 scrollbar-hide">
        <AnimatePresence mode="popLayout">
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center space-y-4 opacity-50">
               <Cpu size={32} className="text-text-tertiary" />
               <p className="text-[10px] font-black uppercase tracking-[0.2em] text-text-tertiary">Awaiting Neural Handshake...</p>
            </div>
          ) : (
            messages.map((msg) => (
              <motion.div
                key={msg.id}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="p-4 rounded-2xl bg-white/[0.02] border border-white/5 hover:border-brand-teal/20 transition-all group"
              >
                <div className="flex justify-between items-center mb-3">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 rounded-lg bg-white/5">
                       {getTeamIcon(msg.team)}
                    </div>
                    <span className="text-[10px] font-black uppercase tracking-widest text-text-primary truncate max-w-[120px]">
                      {msg.sender.replace('_', ' ')}
                    </span>
                  </div>
                  <span className="text-[9px] font-mono font-bold text-text-tertiary">
                    {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                  </span>
                </div>

                <div className="space-y-2">
                  <p className="text-[11px] leading-relaxed text-text-secondary font-medium">
                    {msg.content.reasoning || (typeof msg.content === 'string' ? msg.content : 'Synchronizing node telemetry across teams.')}
                  </p>
                  
                  {msg.type === 'signal' && (
                    <div className="flex items-center gap-2 mt-2">
                       <span className={`px-2 py-0.5 rounded text-[8px] font-black uppercase tracking-tighter ${msg.content.action === 'buy' ? 'bg-brand-teal/20 text-brand-teal' : 'bg-brand-plum/20 text-brand-plum'}`}>
                         {msg.content.action} {msg.content.symbol}
                       </span>
                       <span className="text-[9px] font-mono font-bold text-text-tertiary">{(msg.content.confidence * 100).toFixed(0)}% CONF</span>
                    </div>
                  )}
                </div>
              </motion.div>
            ))
          )}
        </AnimatePresence>
      </div>

      <div className="p-4 bg-bg-primary/50 border-t border-white/5">
         <div className="flex items-center gap-3 px-4 py-2 bg-white/5 rounded-xl border border-white/10 group-focus-within:border-brand-teal/50 transition-all">
            <span className="text-[10px] font-black text-brand-teal-light uppercase tracking-widest">DIR:</span>
            <input 
              type="text" 
              placeholder="Inject operator override..." 
              className="bg-transparent border-none focus:ring-0 text-[11px] font-medium text-white placeholder-text-tertiary w-full"
            />
         </div>
      </div>
    </GlassCard>
  );
};

export default A2ANeuralFeed;
